import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, App } from 'antd';
import type { TerrainType } from '../../types/types';
import { logger } from '../../services/logger.client';
interface AddTerrainTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTerrainType: (terrainType: Omit<TerrainType, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

const AddTerrainTypeModal: React.FC<AddTerrainTypeModalProps> = ({ isOpen, onClose, onAddTerrainType }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  useEffect(() => {
    if (isOpen) {
      // Reset form and set a default color when modal opens
      form.resetFields();
      form.setFieldsValue({ color: '#228B22' }); // A default green
    }
  }, [isOpen, form]);

  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      await onAddTerrainType({ name: values.name, color: values.color });
      message.success(`Terrain type "${values.name}" has been added.`);
      // No need to reset form here as the useEffect handles it on next open
      onClose();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'info' in error) {
        // Antd form validation error
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      message.error(`Failed to add terrain type: ${errorMessage}`);
      logger.warn({ formValues: form.getFieldsValue(), errorInfo: error }, 'Add Terrain Type failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add New Terrain Type"
      confirmLoading={loading}
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      destroyOnHidden
    >      
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} name="add_terrain_type_form">
        <Form.Item
          name="name"
          label="Terrain Name"
          rules={[{ required: true, message: 'Please input the name for the terrain!' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="color"
          label="Color"
          rules={[{ required: true, message: 'Please select a color!' }]}
        >
          <Input type="color" style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddTerrainTypeModal;